import apiClient from '../client';
import type { ReportSchema, WithAgentPublicUserData } from '@appTypes';

export interface MediaImage {
  url: string;
  type: 'before' | 'after';
}

export interface MediaReport {
  reportId: string;
  clientName: string;
  date: number;
  images: MediaImage[];
}

// SAV Reports API
export type { ReportSchema, WithAgentPublicUserData } from '@appTypes';

type ReportWithUserData = WithAgentPublicUserData<ReportSchema>;

/**
 * Fetch paginated reports (first page)
 */
export type ReportFilters = {
  status?: string;
  interventionsType?: string;
  warrantyStatuses?: string[];
  minPrice?: number;
  maxPrice?: number;
  minDate?: string;
  maxDate?: string;
};

function filtersToParams(filters?: ReportFilters) {
  if (!filters) return {};
  return {
    ...(filters.status && { status: filters.status }),
    ...(filters.interventionsType && { interventionsType: filters.interventionsType }),
    ...(filters.warrantyStatuses?.length && { warrantyStatuses: filters.warrantyStatuses }),
    ...(filters.minPrice != null && { minPrice: filters.minPrice }),
    ...(filters.maxPrice != null && { maxPrice: filters.maxPrice }),
    ...(filters.minDate && { minDate: filters.minDate }),
    ...(filters.maxDate && { maxDate: filters.maxDate }),
  };
}

/**
 * Numbered-page mode: returns an ordered array + exact total.
 * Not usable with `search` (the search index is cursor-only).
 * `page` is 0-based.
 */
export async function fetchReportsPage(params: ReportFilters & { page: number; limit: number }): Promise<{ data: ReportWithUserData[]; total: number }> {
  const response: any = await apiClient.get('/sav/reports', {
    params: {
      page: params.page,
      limit: params.limit,
      ...filtersToParams(params),
    },
  });
  return { data: response.data ?? [], total: response.total ?? 0 };
}

export async function fetchPaginatedReports(params?: ReportFilters & { search?: string; lastDocumentId?: string }): Promise<{ data: { [key: string]: ReportWithUserData }; lastDocumentId?: string } | undefined> {
  const response: any = await apiClient.get('/sav/reports', {
    params: {
      paginated: true,
      lastDocumentId: params?.lastDocumentId,
      ...(params?.search && { search: params.search }),
      ...filtersToParams(params),
    },
  });
  return { data: response.data, lastDocumentId: response.lastDocumentId };
}

/**
 * Get a specific report by ID
 */
export async function getReportById(params: { reportId: string }): Promise<ReportWithUserData | undefined> {
  const response: any = await apiClient.get(`/sav/reports/${params.reportId}`);
  return response.data;
}

/**
 * Mark report as seen
 */
export async function seeReport(params: { report: ReportWithUserData }): Promise<void> {
  await apiClient.post(`/sav/reports/${params.report.reportId}/see`, {
    report: params.report,
  });
}

/**
 * Update a report
 */
export async function updateReport(params: {
  report: ReportWithUserData;
  newData: Partial<ReportSchema>;
}): Promise<ReportWithUserData | undefined> {
  const response: any = await apiClient.put(`/sav/reports/${params.report.reportId}`, {
    report: params.report,
    newData: params.newData,
  });
  return response.data;
}

/**
 * Delete a report
 */
export async function deleteReport(params: { report: ReportWithUserData }): Promise<void> {
  await apiClient.delete(`/sav/reports/${params.report.reportId}`, {
    data: { report: params.report },
  });
}

/**
 * Send a WhatsApp template message to the report's client.
 * templateName and templateLanguage must match a Meta-approved template.
 * hasParams: true → backend injects clientName, reportDate, productName into the body.
 */
export async function sendReportFeedbackWhatsApp(params: {
  report: ReportWithUserData;
  templateName: string;
  templateLanguage: string;
  hasParams: boolean;
}): Promise<{ messageId: string }> {
  const response: any = await apiClient.post(`/sav/reports/${params.report.reportId}/send-feedback-whatsapp`, {
    report:           params.report,
    templateName:     params.templateName,
    templateLanguage: params.templateLanguage,
    hasParams:        params.hasParams,
  });
  return response.data;
}

/**
 * Mark report as accounted
 */
export async function markReportAccounted(params: { report: ReportWithUserData }): Promise<ReportWithUserData | undefined> {
  const response: any = await apiClient.post(`/sav/reports/${params.report.reportId}/mark-accounted`, {
    report: params.report,
  });
  return response.data;
}

/**
 * Generate Word document for a report
 * Downloads the generated document as a file
 */
export async function generateReport(params: { report: ReportWithUserData }): Promise<void> {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1';
  const token = localStorage.getItem('authToken');

  const response = await fetch(`${apiBaseUrl}/sav/reports/${params.report.reportId}/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    },
    body: JSON.stringify({ report: params.report }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || 'Failed to generate report');
  }

  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('Content-Disposition');
  let fileName = 'report.docx';
  if (contentDisposition) {
    const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match && match[1]) {
      fileName = decodeURIComponent(match[1].replace(/['"]/g, ''));
    }
  }

  // Download the file
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Open report file (no-op in web version - uses download instead)
 */
export function openReportFile(params: { report: ReportWithUserData }): void {
  // In web version, we download the file instead of opening
  console.info('openReportFile: Use generateReport to download the document');
}

/**
 * Open reports folder (no-op in web version)
 */
export function openReportsFolder(): void {
  console.warn('openReportsFolder is not available in web version');
}

/**
 * Export reports as an Excel file (.xlsx).
 * Pass reportIds to export specific reports, or pass filters to export a filtered set.
 * If neither is provided, all reports are exported.
 */
export async function exportReports(params: { reportIds?: string[] } & ReportFilters = {}): Promise<void> {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1';
  const token = localStorage.getItem('authToken');

  const response = await fetch(`${apiBaseUrl}/sav/reports/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Failed to export reports');
  }

  const contentDisposition = response.headers.get('Content-Disposition');
  let fileName = `rapports_${new Date().toISOString().slice(0, 10)}.xlsx`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match?.[1]) fileName = decodeURIComponent(match[1].replace(/['"]/g, ''));
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Fetch paginated reports with extracted images for the media grid modal.
 */
export async function fetchReportsMedia(params?: { lastDocumentId?: string }): Promise<{
  data: MediaReport[];
  lastDocumentId?: string;
}> {
  const response: any = await apiClient.get('/sav/reports/media', {
    params: {
      ...(params?.lastDocumentId && { lastDocumentId: params.lastDocumentId }),
    },
  });
  return { data: response.data ?? [], lastDocumentId: response.lastDocumentId };
}

/**
 * Check if a report document has been generated on the server
 */
export async function isReportGenerated(params: { report: ReportWithUserData }): Promise<boolean> {
  try {
    const response: any = await apiClient.get(`/sav/reports/${params.report.reportId}/is-generated`);
    return response.data ?? false;
  } catch (error) {
    console.error('Error checking if report is generated:', error);
    return false;
  }
}
